import { useCallback } from "react";
import { useMutation, useQueryCache } from "react-query";
import { postAuthAnonymousTokenIssueViaOtp } from "../../../queries/auth";
import { coreService } from "../../../services/coreService";
import { FirstArgumentType } from "../../../types/ArgumentTypes";
import { buildNotifySubscriberContactMethodsQueryKey } from "../../notifications/hooks/useNotifySubscriberContactMethodsForLoggedInUser";
import { useAnonymousToken } from "./useAnonymousToken";
import { useAnonymousTokenNetworkHeaders } from "./useAnonymousTokenNetworkHeaders";

export function useAuthIssueAnonymousTokenByOTP({
  onSuccess = () => {},
  onError = () => {},
}: {
  onSuccess?: (subscriberContactMethodId: number) => void;
  onError?: (
    error: unknown,
    message: string,
    errorCode: "RATE_LIMIT" | "MAX_ATTEMPTS_REACHED" | "EXPIRED" | "NOT_FOUND" | "WRONG" | null,
  ) => void;
} = {}) {
  const login = useAnonymousToken((state) => state.login);
  const networkHeaders = useAnonymousTokenNetworkHeaders();
  const queryCache = useQueryCache();

  const [authIssueAnonymousTokenMutation, { isLoading: isAuthIssueAnonymousTokenLoading }] = useMutation(
    postAuthAnonymousTokenIssueViaOtp(networkHeaders),
    {
      onSuccess: (data) => {
        const { token, subscriberContactMethodId } = data;

        // Perform login
        login(token, subscriberContactMethodId);

        queryCache.invalidateQueries(buildNotifySubscriberContactMethodsQueryKey());

        // Trigger the onSuccess callback
        onSuccess(subscriberContactMethodId);
      },
      onError: (error) => {
        const status = (error as any).status;
        const errorCode = (error as any).error.errorCode;

        switch (status) {
          case 403:
            if (errorCode === "MAX_ATTEMPTS_REACHED") {
              onError(error, "Maximum attempts reached", "MAX_ATTEMPTS_REACHED");
            } else if (errorCode === "EXPIRED") {
              onError(error, "One-time password is expired", "EXPIRED");
            } else if (errorCode === "NOT_FOUND") {
              onError(error, "One-time password is expired", "NOT_FOUND");
            } else if (errorCode === "WRONG") {
              onError(error, "One-time password does not match", "WRONG");
            } else {
              onError(error, "One-time password is wrong or expired", null);
            }
            break;
          case 429:
            const message = (error as any).error.message;
            onError(error, message, "RATE_LIMIT");
            break;
          default:
            onError(error, "An unknown error occurred while verifying one-time password", null);
            break;
        }
      },
    },
  );

  const issueAnonymousTokenByOTP = useCallback(
    async (body: FirstArgumentType<typeof coreService.authAnonymousTokenIssueViaOtpNotifyCreate>) => {
      return await authIssueAnonymousTokenMutation(body);
    },
    [authIssueAnonymousTokenMutation],
  );

  return { issueAnonymousTokenByOTP, isAuthIssueAnonymousTokenLoading };
}
